import { ChangeEvent, FC, useEffect, useMemo } from 'react';
// Models
import { ICurrentAccount } from 'app/models/Account';
import { ICollection } from 'app/store/DMSCollections/DMSCollections.models';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import { DMSBatchesActions } from 'app/store/DMSBatches/DMSBatches.slice';
import { DMSCollectionsActions } from 'app/store/DMSCollections/DMSCollections.slice';
// Selectors
import { selectCurrentAccount } from 'app/store/Accounts/Accounts.selectors';
import { selectCollections, selectSelectedCollectionId } from 'app/store/DMSCollections/DMSCollections.selectors';
import { selectFilter, selectCollectionTotalDocuments } from 'app/store/DMSBatches/DMSBatches.selectors';
// Components
import { Loader } from 'app/components/Utilities';
import { Select } from 'app/components/Mui';
// i18next
import { useTranslation } from 'react-i18next';
import LocalStorageService from 'app/services/LocalStorage.service';

const CollectionSelect:FC = () => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const currentAccount:ICurrentAccount | null = useAppSelector(selectCurrentAccount);
  const collections:ICollection[] | null = useAppSelector(selectCollections);
  const collectionTotalDocuments = useAppSelector(selectCollectionTotalDocuments);
  const selectedCollectionId = useAppSelector(selectSelectedCollectionId);
  const filter = useAppSelector(selectFilter);

  const options = useMemo(() => {
    if ( !collections ) return [];

    const opts = collections.map((collection:ICollection) => {
      const total = collectionTotalDocuments?.[collection.id] ?? 0;
      
      return {
        id: collection.id,
        name: `${collection.name}${collectionTotalDocuments ? ` (${total})` : ''}`,
      };
    });

    if ( collections.length === 1 ) return opts;

    const allTotal = Object.values(collectionTotalDocuments ?? {}).reduce(
      (acc, cur) => acc + cur,
      0
    );

    return [
      { id: 0, name: `${t('labels.all')}${collectionTotalDocuments ? ` (${allTotal})` : ''}` },
      ...opts,
    ];
    // eslint-disable-next-line
  }, [collections, collectionTotalDocuments]);

  useEffect(() => {
    if (typeof selectedCollectionId !== 'undefined') {
      setCollectionId(selectedCollectionId);
    }
    // eslint-disable-next-line
  }, [selectedCollectionId]);

  const setCollectionId = (nextValue:number) => {
    dispatch(DMSBatchesActions.setFilter({ field: 'collectionId', value: nextValue }));

    if ( !currentAccount ) return;

    LocalStorageService.setCollectionTab(currentAccount.id, nextValue);
    dispatch(DMSCollectionsActions.setSelectedCollectionId(nextValue));
  };

  const handleChange = (e:ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setCollectionId(value);
  }

  if ( !options.length || typeof filter.collectionId === 'undefined' ) return <Loader />;
  return (
    <Select
      label={t('labels.collection')}
      value={filter.collectionId}
      options={options}
      onChange={handleChange}
      fullWidth
      size="small"
    />
  );
};

export default CollectionSelect;
